//Proofed
import { useState } from 'react'
import {
    Box, Container, Typography, TextField, Stack, Card, CardContent,
    FormGroup, FormControlLabel, Checkbox, Alert, CircularProgress,
} from '@mui/material'
import EmailIcon from '@mui/icons-material/Email'
import { COLOR_PRIMARY, NAVBAR_BG, DARK_700, BG_LIGHT } from '../theme'
import { FancyGoldButton } from '../components/FancyGoldButton'
import PageMeta from '../components/PageMeta'
import { buildCoreApiUrl } from '../common/common'
import posthog, { isPosthogEnabled, posthogLogger } from '../posthog'

const PRODUCTS = [
    { id: 'core', label: 'CFP Core - Tender Alerting' },
    { id: 'ninja', label: 'Tender Ninja - Bid Writing Tool' },
    { id: 'analytics', label: 'Analytics - Market Intelligence' },
    { id: 'bidservices', label: 'Bid Services - Bid Writing or Bid Support' },
]

type Status = 'idle' | 'loading' | 'success' | 'error'

export default function ContactUs() {
    const [name, setName] = useState('')
    const [email, setEmail] = useState('')
    const [telephone, setTelephone] = useState('')
    const [selected, setSelected] = useState<string[]>([])
    const [message, setMessage] = useState('')
    const [status, setStatus] = useState<Status>('idle')
    const [errorMsg, setErrorMsg] = useState('')

    const toggleProduct = (id: string) =>
        setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])

    const handleSubmit = async () => {
        if (!name.trim()) { setErrorMsg('Please enter your name'); return }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setErrorMsg('Please enter a valid email address'); return }
        if (selected.length === 0) { setErrorMsg('Please select at least one product you are interested in'); return }
        setErrorMsg('')
        setStatus('loading')
        try {
            const productLabels = selected.map(id => PRODUCTS.find(p => p.id === id)?.label ?? id).join(', ')
            const fullMessage = `Products: ${productLabels}${message.trim() ? `\n\n${message.trim()}` : ''}`
            const res = await fetch(buildCoreApiUrl(
                `api/contactus?n=${encodeURIComponent(name)}&e=${encodeURIComponent(email)}&p=${encodeURIComponent(telephone)}&m=${encodeURIComponent(fullMessage)}`
            ))
            if (res.ok) {
                if (isPosthogEnabled) {
                    posthog.capture('contact_form_submitted', { product_ids: selected })
                }
                posthogLogger.info('contact_form_submission_succeeded', {
                    selected_product_count: selected.length,
                })
                setStatus('success')
            } else {
                setErrorMsg('Something went wrong. Please email us directly at sales@contractfinderpro.com')
                setStatus('error')
            }
        } catch {
            setErrorMsg('Could not send your message. Please email us directly at sales@contractfinderpro.com')
            setStatus('error')
        }
    }

    return (
        <Box>
            <PageMeta
                title="Contact Us"
                description="Get in touch with the Contract Finder Pro team. Call 0333 772 0250 or send us a message for questions about our platform, pricing or advisory services."
                canonical="/contactus"
            />
            {/* Hero */}
            <Box sx={{
                background: `linear-gradient(135deg, ${NAVBAR_BG} 0%, ${DARK_700} 100%)`,
                pt: { xs: 4, md: 6 },
                pb: { xs: 4, md: 5 },
                textAlign: 'center',
            }}>
                <Container maxWidth="md">
                    <Typography variant="h1" sx={{ color: 'white', fontSize: { xs: '36px', md: '48px' }, mb: 2 }}>
                        Get in Touch
                    </Typography>
                    <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: '18px' }}>
                        Tell us which products you are interested in and we will be in touch shortly
                    </Typography>
                </Container>
            </Box>

            {/* Form */}
            <Box sx={{ backgroundColor: BG_LIGHT, py: { xs: 3, md: 5 } }}>
                <Container maxWidth="sm">
                    <Card elevation={2} sx={{ borderRadius: 3 }}>
                        <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                            {status === 'success' ? (
                                <Box sx={{ textAlign: 'center', py: 4 }}>
                                    <EmailIcon sx={{ fontSize: 56, color: COLOR_PRIMARY, mb: 2 }} />
                                    <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>Message Sent!</Typography>
                                    <Typography color="text.secondary">
                                        Thank you for getting in touch. A member of our team will be in contact shortly.
                                    </Typography>
                                </Box>
                            ) : (
                                <Stack spacing={3}>
                                    <Typography variant="h6" fontWeight={700}>Contact Us</Typography>

                                    <TextField
                                        label="Full Name"
                                        value={name}
                                        onChange={e => setName(e.target.value)}
                                        fullWidth
                                        required
                                    />
                                    <TextField
                                        label="Email Address"
                                        type="email"
                                        value={email}
                                        onChange={e => setEmail(e.target.value)}
                                        fullWidth
                                        required
                                    />
                                    <TextField
                                        label="Telephone (optional)"
                                        type="tel"
                                        value={telephone}
                                        onChange={e => setTelephone(e.target.value)}
                                        fullWidth
                                    />

                                    <Box>
                                        <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                                            Which products are you interested in? *
                                        </Typography>
                                        <FormGroup>
                                            {PRODUCTS.map(p => (
                                                <FormControlLabel
                                                    key={p.id}
                                                    control={
                                                        <Checkbox
                                                            checked={selected.includes(p.id)}
                                                            onChange={() => toggleProduct(p.id)}
                                                            sx={{ '&.Mui-checked': { color: COLOR_PRIMARY } }}
                                                        />
                                                    }
                                                    label={p.label}
                                                />
                                            ))}
                                        </FormGroup>
                                    </Box>

                                    <TextField
                                        label="Message (optional)"
                                        value={message}
                                        onChange={e => setMessage(e.target.value)}
                                        fullWidth
                                        multiline
                                        rows={4}
                                    />

                                    {(errorMsg || status === 'error') && (
                                        <Alert severity="error">{errorMsg}</Alert>
                                    )}

                                    <FancyGoldButton
                                        onClick={handleSubmit}
                                        disabled={status === 'loading'}
                                        fullWidth
                                    >
                                        {status === 'loading'
                                            ? <CircularProgress size={22} sx={{ color: 'inherit' }} />
                                            : 'Send Message'}
                                    </FancyGoldButton>

                                    <Typography variant="caption" color="text.secondary" textAlign="center">
                                        Or email us directly at{' '}
                                        <a href="mailto:sales@contractfinderpro.com" style={{ color: COLOR_PRIMARY }}>
                                            sales@contractfinderpro.com
                                        </a>
                                    </Typography>
                                </Stack>
                            )}
                        </CardContent>
                    </Card>
                </Container>
            </Box>
        </Box>
    )
}
